/**
 * Ruleset Feature Flags
 *
 * Defines what features each ruleset supports.
 * Components use these flags to conditionally render UI elements.
 */

export interface RulesetFeatures {
  /** Character creation features */
  creation: {
    /** Whether the ruleset uses tiered feats instead of standard pick-from-list */
    hasTieredFeats: boolean;
    /** Whether classes have faction context (e.g., The Circle, The Shunned) */
    hasFactionContext: boolean;
    /** Whether species can have multiple variants (e.g., Aurora Elf, Forest Dwarf) */
    hasSpeciesVariants: boolean;
  };
  /** UI customization */
  ui: {
    /** Terminology overrides */
    terminology: {
      /** What to call races (Race, Species, Ancestry) */
      race: string;
      /** Plural form (Races, Species, Ancestries) */
      racePlural: string;
      /** What to call subraces (Subrace, Lineage, Heritage) */
      subrace: string;
      /** Plural form (Subraces, Lineages, Heritages) */
      subracePlural: string;
    };
  };
}

const DEFAULT_FEATURES: RulesetFeatures = {
  creation: {
    hasTieredFeats: false,
    hasFactionContext: false,
    hasSpeciesVariants: false,
  },
  ui: {
    terminology: {
      race: 'Race',
      racePlural: 'Races',
      subrace: 'Subrace',
      subracePlural: 'Subraces',
    },
  },
};

export const rulesetFeatures: Record<string, RulesetFeatures> = {
  'srd-5.1': {
    ...DEFAULT_FEATURES,
  },
  ignus: {
    creation: {
      hasTieredFeats: true,
      hasFactionContext: true,
      hasSpeciesVariants: true,
    },
    ui: {
      terminology: {
        race: 'Species',
        racePlural: 'Species',
        subrace: 'Lineage',
        subracePlural: 'Lineages',
      },
    },
  },
};

/**
 * Get features for a ruleset, with fallback to defaults
 */
export function getRulesetFeatures(rulesetId: string): RulesetFeatures {
  return rulesetFeatures[rulesetId] ?? DEFAULT_FEATURES;
}
